import { Plus } from "lucide-react";
import CoinFluxi from "../../assets/icons/coin-fluxi.svg";
import { Card, CardContent } from "../ui/card";
import { ProgressBar } from "../ui/progressBar";
import type { CreditsDetails } from "../../types/membership";
import { useAuth } from "../../context/AuthContext";
import { dictionary, Language } from "../../utils/dictionary";
import { ButtonFluxi } from "../common/Buttons/ButtonFluxi/ButtonFluxi";
import { Alert } from "../common/information/Alert";
import { convertToZone } from "../../utils/formatters";
import { format } from "date-fns";
import { useState } from "react";
import { ModalWrapper } from "../modals/ModalWrapper";
import { BuyCreditsModal } from "../modals/BuyCreditsModal";

interface CreditsSectionProps {
  credits: CreditsDetails;
  nextRenewalDate: string;
  variant?: "default" | "compact";
}

export function CreditsSection({
  credits,
  nextRenewalDate,
  variant = "default",
}: CreditsSectionProps) {
  const { language } = useAuth();
  const formatDate = "dd/MM/yyyy";

  const [isOpenBuyCreditsModal, setIsOpenBuyCreditsModal] = useState(false);

  const closeModal = () => {
    setIsOpenBuyCreditsModal(false);
  };

  const nextRenewalDateFormatted = convertToZone(
    format(new Date(nextRenewalDate || ""), formatDate),
    formatDate
  );

  const formatNumber = (num: number) => {
    return num.toLocaleString("es-ES");
  };

  const handleBuyCredits = () => {
    setIsOpenBuyCreditsModal(true);
  };

  const getShortCreditWarningText = (lang: Language): string => {
    switch (lang) {
      case Language.SPANISH:
        return "Debes gastarlos antes de";
      case Language.ENGLISH:
        return "You must spend them before";
      case Language.PORTUGUESE:
        return "Você deve gastá-los antes de";
      default:
        return "Debes gastarlos antes de";
    }
  };

  const buyCreditsModal = isOpenBuyCreditsModal && (
    <ModalWrapper isOpen={isOpenBuyCreditsModal} onClose={closeModal}>
      <BuyCreditsModal
        onClose={closeModal}
        nextRenewalDate={nextRenewalDate}
        language={language}
      />
    </ModalWrapper>
  );

  if (variant === "compact") {
    return (
      <>
        {buyCreditsModal}
        <div className="border-b border-gray-100 py-4">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <img
                src={CoinFluxi}
                alt="credits"
                className="h-5 w-5"
                style={{ filter: "brightness(0) saturate(100%) invert(67%) sepia(54%) saturate(1000%) hue-rotate(5deg) brightness(98%) contrast(89%)" }}
              />
              <h2 className="text-base font-semibold text-gray-950">
                {dictionary["credits"][language]}
              </h2>
            </div>
            <button
              type="button"
              onClick={handleBuyCredits}
              className="inline-flex h-9 items-center gap-2 rounded-xl bg-gray-950 px-3 text-sm font-semibold text-white transition-colors hover:bg-gray-800"
            >
              <Plus className="h-4 w-4" />
              {dictionary["buy-credits"][language]}
            </button>
          </div>

          <div className="grid gap-4 lg:grid-cols-[190px_1fr] lg:items-center">
            <div>
              <p className="text-3xl font-semibold text-gray-950">
                {formatNumber(credits.credits_available)}
              </p>
              <p className="mt-1 text-sm text-gray-500">
                de {formatNumber(credits.total_credits)} {dictionary["available"][language]}
              </p>
            </div>
            <div>
              <div className="mb-2 flex items-center justify-between text-sm">
                <span className="text-gray-500">
                  {formatNumber(credits.credits_used)} {dictionary["credits-used"][language].toLowerCase()}
                </span>
                <span className="font-semibold text-gray-950">{credits.percentage_used}%</span>
              </div>
              <ProgressBar value={credits.percentage_used} max={100} />
              <p className="mt-2 text-xs text-gray-500">
                {getShortCreditWarningText(language)}{" "}
                <span className="font-semibold text-gray-700">{nextRenewalDateFormatted}</span>
              </p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {buyCreditsModal}
      <Card>
        <CardContent className="p-4 md:p-6">
          <div className="flex items-center mb-4 md:mb-6 justify-between align-middle gap-2">
            <div className="flex items-center gap-2">
              <img src={CoinFluxi} alt="credits" className="w-4 h-4 md:w-5 md:h-5" style={{ filter: "brightness(0) saturate(100%) invert(67%) sepia(54%) saturate(1000%) hue-rotate(5deg) brightness(98%) contrast(89%)" }} />
              <h2 className="text-xs md:text-sm font-semibold text-gray-900">
                {dictionary["credits"][language]}
              </h2>
            </div>
            <ButtonFluxi
              icon={<Plus className="w-4 h-4 md:w-5 md:h-5 text-white" />}
              text={dictionary["buy-credits"][language]}
              onClick={handleBuyCredits}
              style={{
                fontSize: "11px",
                paddingTop: "8px",
                paddingBottom: "8px",
                paddingLeft: "12px",
                paddingRight: "12px",
                maxHeight: "36px",
              }}
            />
          </div>

          {/* Mobile Layout */}
          <div className="md:hidden">
            {/* Credits Display with Warning Message */}
            <div className="flex flex-row items-center gap-3 mb-6">
              {/* Credits Display: "14" centered, takes less space */}
              <div className="flex flex-col items-center justify-center flex-shrink-0 max-w-[60px]">
                <div className="text-4xl font-bold text-gray-900 mb-1 text-center">
                  {formatNumber(credits.credits_available)}
                </div>
                <p className="text-sm text-gray-600 text-center">
                  de {formatNumber(credits.total_credits)}{" "}
                  {dictionary["available"][language]}
                </p>
              </div>

              {/* Warning Message next to numbers - shorter text */}
              <div className="flex-1 min-w-0">
                <Alert
                  title={dictionary["remember-use-credits"][language]}
                  description={
                    <>
                      {getShortCreditWarningText(language)}{" "}
                      <strong className="font-bold">
                        {nextRenewalDateFormatted}
                      </strong>
                    </>
                  }
                  type="warning"
                  className="[&_h3]:text-xs [&_p]:text-xs p-[0.8rem]"
                />
              </div>
            </div>

            {/* Progress Bar with "6 usados" and "30%" */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-600">
                  {formatNumber(credits.credits_used)}{" "}
                  {dictionary["credits-used"][language].toLowerCase()}
                </span>
                <span className="text-sm font-medium text-gray-900">
                  {credits.percentage_used}%
                </span>
              </div>
              <ProgressBar value={credits.percentage_used} max={100} />
            </div>

            {/* Three Columns: USADOS, RESTANTES, TOTAL */}
            <div className="grid grid-cols-3 gap-3 pt-4 border-t border-gray-200">
              <div className="text-center">
                <div className="text-xs text-gray-600 mb-1.5 font-normal">
                  {dictionary["used"]?.[language]?.toUpperCase() || "USADOS"}
                </div>
                <div className="text-base font-medium text-gray-900">
                  {formatNumber(credits.credits_used)}
                </div>
              </div>
              <div className="text-center">
                <div className="text-xs text-gray-600 mb-1.5 font-normal">
                  {dictionary["remaining"]?.[language]?.toUpperCase() ||
                    "RESTANTES"}
                </div>
                <div className="text-base font-medium text-gray-900">
                  {formatNumber(credits.credits_available)}
                </div>
              </div>
              <div className="text-center">
                <div className="text-xs text-gray-600 mb-1.5 font-normal">
                  {dictionary["total"]?.[language]?.toUpperCase() || "TOTAL"}
                </div>
                <div className="text-base font-bold text-gray-900">
                  {formatNumber(credits.total_credits)}
                </div>
              </div>
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="hidden md:block">
            <div className="text-center mb-4 md:mb-6">
              <div className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                {formatNumber(credits.credits_available)}
              </div>
              <p className="text-sm md:text-base text-gray-600">
                de {formatNumber(credits.total_credits)}{" "}
                {dictionary["available"][language]}
              </p>
            </div>

            <div className="mb-6">
              <Alert
                title={dictionary["remember-use-credits"][language]}
                description={
                  <>
                    {
                      dictionary["you-must-spend-these-credits-before"][
                        language
                      ]
                    }{" "}
                    <strong className="font-bold">
                      {nextRenewalDateFormatted}
                    </strong>{" "}
                    {dictionary["when-your-credits-will-be-renewed"][language]}
                  </>
                }
                type="warning"
              />
            </div>

            <div className="space-y-4">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-600">
                    {dictionary["credits-usage"][language]}
                  </span>
                  <span className="text-sm font-medium text-gray-900">
                    {credits.percentage_used}%
                  </span>
                </div>
                <ProgressBar value={credits.percentage_used} max={100} />
              </div>

              <div className="grid grid-cols-1 gap-3 pt-4 border-t border-gray-200">
                <div className="flex justify-between">
                  <span className="text-gray-600">
                    {dictionary["credits-used"][language]}
                  </span>
                  <span className="font-medium text-gray-900">
                    {formatNumber(credits.credits_used)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">
                    {dictionary["credits-remaining"][language]}
                  </span>
                  <span className="font-medium text-gray-900">
                    {formatNumber(credits.credits_available)}
                  </span>
                </div>
                <div className="flex justify-between border-t border-gray-200 pt-3">
                  <span className="font-medium text-gray-900">
                    {dictionary["plan-total"][language]}
                  </span>
                  <span className="font-bold text-gray-900">
                    {formatNumber(credits.total_credits)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
}
